import React from 'react';
import { Navigate } from 'react-router';
import { RoleType, Permission } from '../../config/roles';
import { useAuth } from '../../context/AuthContext';

interface RoleBasedAccessProps {
  children: React.ReactNode;
  requiredRoles?: RoleType[];
  requiredPermissions?: Permission[];
  redirectPath?: string;
}

/**
 * Role-Based Access Control Component
 * 
 * This component restricts access to children based on user roles or permissions.
 * If the user doesn't have the required roles or permissions, they are redirected.
 * 
 * Usage:
 * 
 * <RoleBasedAccess requiredRoles={[RoleType.ADMIN]}>
 *   <AdminDashboard />
 * </RoleBasedAccess>
 * 
 * Or with permissions:
 * 
 * <RoleBasedAccess requiredPermissions={[Permission.MANAGE_USERS]}>
 *   <UserManagement />
 * </RoleBasedAccess>
 */
export const RoleBasedAccess: React.FC<RoleBasedAccessProps> = ({
  children,
  requiredRoles = [],
  requiredPermissions = [],
  redirectPath = '/dashboard'
}) => {
  const { isAuthenticated, userInfo } = useAuth();
  
  // Check if user is authenticated using context state
  if (!isAuthenticated) {
    // Optional: Log why redirecting
    console.log('[RoleBasedAccess] User not authenticated. Redirecting to login.');
    return <Navigate to="/login" replace />;
  }
  
  // Check roles if required, using userInfo from context
  if (requiredRoles.length > 0) {
    const userRoles = userInfo?.roles || [];
    const hasRequiredRole = requiredRoles.some(role => userRoles.includes(role));
    if (!hasRequiredRole) {
      // Optional: Log why redirecting
      console.log(`[RoleBasedAccess] User lacks required roles (${requiredRoles.join(', ')}). Redirecting to ${redirectPath}. User roles: ${userRoles.join(', ')}`);
      return <Navigate to={redirectPath} replace />; 
    }
  }
  
  // Remove permission check block as UserInfo does not contain permissions
  // Check permissions if required, using userInfo from context (assuming permissions exist)
  // if (requiredPermissions.length > 0) {
  //   const userPermissions = userInfo?.permissions || []; // Assume userInfo might have permissions
  //   const hasRequiredPermission = requiredPermissions.some(permission => 
  //     userPermissions.includes(permission)
  //   );
  //   if (!hasRequiredPermission) {
  //     // Optional: Log why redirecting
  //     console.log(`[RoleBasedAccess] User lacks required permissions (${requiredPermissions.join(', ')}). Redirecting to ${redirectPath}. User permissions: ${userPermissions.join(', ')}`);
  //     return <Navigate to={redirectPath} replace />;
  //   }
  // }
  
  // User has required roles/permissions, render children
  return <>{children}</>;
};

export default RoleBasedAccess;